/**
 * CONDITION REFERENCE SCANNING — which rules depend on a given field path?
 *
 * Used by the field-deletion flow: deleting a field whose path is referenced by an
 * approval-flow trigger condition or by another field's / section's visibleIf must never
 * happen SILENTLY (repo invariant: never silently skip a governance gate). These helpers
 * let the editors name exactly what a deletion will affect, so the admin confirms with
 * eyes open.
 *
 * Pure functions over plain objects — unit-tested in conditionPaths.test.js.
 */

/** Every dotted path referenced anywhere in a condition tree (all/any/not/some/leaf). */
export function collectConditionPaths(condition, acc = new Set()) {
  if (!condition || typeof condition === 'boolean') return acc;
  if (Array.isArray(condition.all)) {
    condition.all.forEach((c) => collectConditionPaths(c, acc));
    return acc;
  }
  if (Array.isArray(condition.any)) {
    condition.any.forEach((c) => collectConditionPaths(c, acc));
    return acc;
  }
  if (condition.not !== undefined) return collectConditionPaths(condition.not, acc);
  if (typeof condition.path === 'string' && condition.path) acc.add(condition.path);
  // `some` quantifies over rows at `path` (already added above); its `value` is a
  // sub-condition over row-relative paths, which we do not attribute to top-level fields.
  return acc;
}

/**
 * Paths a field WRITES beyond its own path. Some controls store their answer in sibling
 * paths — most importantly the Matter Type control at `matterType.selection`, whose real
 * outputs are the `isLitigation` / `isNonLitigation` booleans that approval triggers,
 * page reveals, and the fees section all condition on. Deleting the control kills those
 * writes, so reference scanning must consider them or the most consequential deletion in
 * the form would produce zero warnings.
 */
const WRITES_SIBLING_PATHS = {
  'matterType.selection': ['matterType.isLitigation', 'matterType.isNonLitigation'],
};

/** Every path whose value disappears if this field is deleted. */
export function pathsWrittenBy(fieldPath) {
  return [fieldPath, ...(WRITES_SIBLING_PATHS[fieldPath] || [])];
}

/**
 * True when `referencedPath` is, or is nested under, any path the field writes —
 * deleting `matterType` also deletes `matterType.selection`, so conditions on either
 * count, and conditions on written sibling paths count too.
 */
function pathCovers(fieldPath, referencedPath) {
  return pathsWrittenBy(fieldPath).some(
    (written) => referencedPath === written || referencedPath.startsWith(`${written}.`)
  );
}

/**
 * Labels of approval-flow steps whose trigger condition references the field.
 * `flow` is the settings/approvalFlow document (or null while loading).
 */
export function approvalStepsReferencing(fieldPath, flow) {
  if (!fieldPath || !flow || !Array.isArray(flow.steps)) return [];
  return flow.steps
    .filter((step) => step && step.enabled !== false)
    .filter((step) =>
      [...collectConditionPaths(step.condition)].some((p) => pathCovers(fieldPath, p))
    )
    .map((step) => step.label || step.key);
}

/**
 * Labels of OTHER fields (and section titles) whose visibleIf references the field —
 * deleting it would freeze their reveal condition on whatever was last stored.
 *
 * IMPORTANT: pass the MERGED builder sections (buildBuilderSections), never the raw code
 * schema — custom fields' reveal conditions and admin-edited visibleIf overrides live
 * only in the merged view, and those are exactly the dependents most likely to be
 * forgotten. Fields the field mirrors into (`derivedFrom`) are reported too.
 */
export function visibleIfDependents(fieldPath, sections) {
  if (!fieldPath || !Array.isArray(sections)) return [];
  const dependents = [];
  for (const section of sections) {
    if (
      section.visibleIf &&
      [...collectConditionPaths(section.visibleIf)].some((p) => pathCovers(fieldPath, p))
    ) {
      dependents.push(`page “${section.title}”`);
    }
    for (const field of section.fields || []) {
      if (field.path === fieldPath) continue;
      if (
        field.visibleIf &&
        [...collectConditionPaths(field.visibleIf)].some((p) => pathCovers(fieldPath, p))
      ) {
        dependents.push(field.label || field.text || field.path);
      } else if (field.derivedFrom && pathCovers(fieldPath, field.derivedFrom)) {
        dependents.push(`${field.label || field.path} (mirrors this field)`);
      }
    }
  }
  return dependents;
}

/**
 * Cross-field submission gates anchored on specific built-in fields (the checks in
 * fieldRules.js structuralProblems / the server's submitTimeErrors). Deleting the anchor
 * deliberately disables the gate — so the confirmation must SAY so. Kept as data here so
 * the delete flow and the validators cannot drift apart silently.
 */
export const STRUCTURAL_GATES = {
  'matterType.selection':
    'the "choose Litigation or Non-Litigation" submission requirement',
  'conflictCheck.hitsAcknowledgedByAttorney':
    'the conflict-hits acknowledgement submission gate',
  'wallBuilder.practiceGroupIds':
    'the Wall Builder "designate at least one group or person" requirement',
  'wallBuilder.personUids':
    'the Wall Builder "designate at least one group or person" requirement',
  'origination.workingAttorneys':
    'the origination split (100% / per-attorney cap) submission checks',
  areaOfLawOther: 'the "describe the Other area of law" submission requirement',
  areaOfLawOtherReason: 'the "explain why Other was selected" submission requirement',
};

/**
 * The complete, named consequence list for deleting a field — the ONE source both the
 * inline editor and the Field Builder render in their confirmations. Pure, so the
 * warning contract is unit-testable.
 *
 * @param {object} field     the merged field being deleted ({path, isCustom, preventClone…})
 * @param {object} args.flow      the LIVE settings/approvalFlow document (null while loading)
 * @param {Array}  args.sections  MERGED builder sections (code + overrides + custom fields)
 * @returns {string[]} human-readable warnings (empty = nothing references the field)
 */
export function buildDeleteWarnings(field, { flow, sections }) {
  if (!field?.path) return [];
  const warnings = [];
  for (const step of approvalStepsReferencing(field.path, flow)) {
    warnings.push(
      `The approval step “${step}” is triggered by this field — without it that approval will no longer be required.`
    );
  }
  const dependents = visibleIfDependents(field.path, sections);
  if (dependents.length) {
    warnings.push(
      `Shows/hides: ${dependents.join(', ')} — their visibility rules depend on this field.`
    );
  }
  const gates = new Set(
    pathsWrittenBy(field.path)
      .map((p) => STRUCTURAL_GATES[p])
      .filter(Boolean)
  );
  for (const gate of gates) {
    warnings.push(`Deleting this field turns off ${gate}.`);
  }
  if (field.isCustom && field.preventClone === true) {
    warnings.push(
      'This field is marked “Never copy”: after deletion its saved answers will no longer be stripped from clones and templates.'
    );
  }
  return warnings;
}
